import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { SuccessNotify, FailedNotify } from "../constants/tostify";
import {
  useGetUserConnectionsQuery,
  useGetUserInfoQuery,
  useUpdateUserProfileMutation,
} from "../features/user/userApiSlice";
import { useGetAllConnectedQuery } from "../features/connection/connectionApiSlice";

export const useUserProfileState = ({ isMarketerTabActive = false } = {}) => {
  const [leftSide, setLeftSidewidth] = useState(7);
  const [rightSide, setRightSide] = useState(5);
  const { name } = useSelector((state) => state.user);
  const authUser = useSelector((state) => state.auth.user);

  const userId = name?._id || authUser?._id || null;
  const [tabsData, setTabsData] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [infooo, setInfooo] = useState();
  const [info, setInfo] = useState();
  const location = useLocation();

  const [createShop, setCreateShop] = useState({
    text: "",
    privacy: "",
  });

  const [createPost, setCreatePost] = useState({
    shopid: "",
    userid: name?._id,
    businessType: "generalPublic",
    my: [
      {
        heading: "",
        content: "",
        img: [""],
        video: [""],
      },
    ],
  });

  const { data: connectionsData, isLoading: connectionsLoading } = useGetUserConnectionsQuery(
    { userid: userId, isMarketerProfile: isMarketerTabActive },
    {
      skip: !userId,
    }
  );

  // Same connected list as Profile Connectivity — used to strip accepted
  // profiles out of right-side tab suggestion strips.
  const { data: allConnectedData } = useGetAllConnectedQuery(
    { userid: userId },
    { skip: !userId },
  );
  // Marketer-scoped accepts are filtered out of the personal getAllConnected
  // response — still hide those people/shops from right-side profile BT tabs.
  const { data: marketerConnectedData } = useGetAllConnectedQuery(
    { userid: userId, isMarketerProfile: true },
    { skip: !userId },
  );

  // Per-tab hide keys: `id|businessType` (and bare id only for generalPublic)
  const connectedHideKeys = useMemo(() => {
    const keys = new Set();
    const addForTab = (id, bt) => {
      if (id == null || id === "" || id === "null") return;
      const sid = String(id);
      const nbt = bt || "generalPublic";
      keys.add(`${sid}|${nbt}`);
      if (nbt === "generalPublic") keys.add(`${sid}|generalPublic`);
    };

    const ingestConnectedPayload = (payload) => {
      (payload?.connectedBT || []).forEach((group) => {
        const bt = group?.businessType || "generalPublic";
        (group?.items || []).forEach((item) => {
          // Marketer Profile card is person-level; also hide them from GP tab
          if (
            bt === "generalPublic" ||
            bt === "marketerProfile" ||
            !item?.shopid
          ) {
            addForTab(item?.userid, "generalPublic");
            if (bt && bt !== "generalPublic") addForTab(item?.userid, bt);
          } else {
            addForTab(item?.shopid, bt);
            addForTab(item?.userid, bt);
            // Shop connect as marketer should also leave GP person strip
            addForTab(item?.userid, "generalPublic");
          }
        });
      });

      (payload?.connected || []).forEach((conn) => {
        const senderId = conn?.senderid ? String(conn.senderid) : null;
        const receiverId = conn?.recieverid ? String(conn.recieverid) : null;
        const senderShop = conn?.sendershopid ? String(conn.sendershopid) : null;
        const receiverShop = conn?.recievershopid
          ? String(conn.recievershopid)
          : null;
        const me = String(userId);
        const recvBT = conn?.businessType || "generalPublic";
        const sendBT = conn?.senderBusinessType || "generalPublic";
        const isMarketerBT = (bt) => {
          const n = String(bt || "")
            .toLowerCase()
            .replace(/[\s_-]+/g, "");
          return (
            n === "marketerprofile" ||
            n === "marketer" ||
            n === "marketershop"
          );
        };

        if (senderId === me) {
          if (!receiverShop || recvBT === "generalPublic" || isMarketerBT(recvBT)) {
            addForTab(receiverId, "generalPublic");
          } else {
            addForTab(receiverShop, recvBT);
            addForTab(receiverId, recvBT);
            addForTab(receiverId, "generalPublic");
          }
          if (isMarketerBT(sendBT) || isMarketerBT(recvBT)) {
            addForTab(receiverId, "generalPublic");
            if (receiverShop) addForTab(receiverShop, recvBT);
          }
        }
        if (receiverId === me) {
          if (!senderShop || sendBT === "generalPublic" || isMarketerBT(sendBT)) {
            addForTab(senderId, "generalPublic");
          } else {
            addForTab(senderShop, sendBT);
            addForTab(senderId, sendBT);
            addForTab(senderId, "generalPublic");
          }
          if (isMarketerBT(sendBT) || isMarketerBT(recvBT)) {
            addForTab(senderId, "generalPublic");
            if (senderShop) addForTab(senderShop, sendBT);
          }
        }
      });
    };

    ingestConnectedPayload(allConnectedData);
    ingestConnectedPayload(marketerConnectedData);

    return keys;
  }, [allConnectedData, marketerConnectedData, userId]);

  const { data: userInfoData, refetch: refetchUserInfo } = useGetUserInfoQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });

  const [updateProfile] = useUpdateUserProfileMutation();

  // Real-time image upload listener for instant profile image updates
  useEffect(() => {
    const handleImageUploaded = (e) => {
      const detail = e?.detail;
      if (detail && (detail.userId === userId || !detail.userId)) {
        const imageUrl = detail.imageUrl;
        if (imageUrl) {
          const imgArr = Array.isArray(imageUrl) ? imageUrl : [imageUrl];
          setInfooo((prev) => (prev ? { ...prev, imageUrl: imgArr } : { imageUrl: imgArr }));
        }
        refetchUserInfo?.();
      }
    };

    window.addEventListener("image-upload-success", handleImageUploaded);
    return () => {
      window.removeEventListener("image-upload-success", handleImageUploaded);
    };
  }, [userId, refetchUserInfo]);

  useEffect(() => {
    if (connectionsData?.data) {
      const responseData = connectionsData.data;
      const dynamicTabs = Object.keys(responseData).map((key) => {
        const rawList = Array.isArray(responseData[key])
          ? responseData[key]
          : [];
        // Drop people/shops already in Connectivity for this tab's BT
        const data = rawList.filter((item) => {
          const profileId = item?._id != null ? String(item._id) : "";
          const profileUserId =
            item?.userid != null ? String(item.userid) : profileId;
          const tabKey = key || "generalPublic";
          if (
            connectedHideKeys.has(`${profileId}|${tabKey}`) ||
            connectedHideKeys.has(`${profileUserId}|${tabKey}`)
          ) {
            return false;
          }
          return true;
        });

        return {
          eventKey: key,
          title:
            key.charAt(0).toUpperCase() +
            key.slice(1).replace(/([A-Z])/g, " $1"),
          data,
        };
      });
      setTabsData(dynamicTabs);
      if (dynamicTabs.length > 0) {
        // Prefer My Timeline ("home") on first load — but never wipe GP / a
        // tab the user is viewing after Share.
        setActiveTab((prev) => {
          if (!prev) return "home";
          if (
            prev === "home" ||
            prev === "generalPublic" ||
            prev === "marketerProfile" ||
            dynamicTabs.some((t) => t.eventKey === prev)
          ) {
            return prev;
          }
          return prev;
        });
      }
    }
  }, [connectionsData, connectedHideKeys]);

  useEffect(() => {
    if (userInfoData?.data?.User) {
      const user = userInfoData.data.User;
      setInfooo((prev) => (prev ? { ...prev, ...user } : user));

      // Normalize description into createShop for edit modal
      const descText = typeof user.description === 'string' ? user.description : user?.description?.text || '';
      setCreateShop((prev) => ({ ...prev, text: descText, privacy: user?.description?.privacy || '' }));
    }
  }, [userInfoData]);

  const handleTabSelect = (eventKey) => {
    setActiveTab(eventKey);
  };

  const getActiveTabData = () => {
    const activeTabData = tabsData.find((tab) => tab.eventKey === activeTab);
    return activeTabData ? activeTabData.data : [];
  };

  const inputChange = (event) => {
    setCreateShop((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };

  const userDatahandler = async () => {
    try {
      const response = await updateProfile({
        id: userId,
        data: {
          // Persist description as a plain string to match the user schema
          description: createShop.text,
          // if needed later, a separate privacy field can be added to the schema
        },
      }).unwrap();

      if (response?.success) {
        SuccessNotify("Successfully!");
        setInfo(response?.data?.User);
        if (response?.data?.User) {
          setInfooo(response.data.User);
        }
      }
    } catch (e) {
      FailedNotify("Failed! Try Again");
    }
  };

  const widthHandler = () => {
    if (leftSide === 7 && rightSide === 5) {
      setLeftSidewidth(1);
      setRightSide(11);
    } else {
      setLeftSidewidth(7);
      setRightSide(5);
    }
  };

  return {
    leftSide,
    rightSide,
    name,
    tabsData,
    activeTab,
    infooo,
    setInfooo,
    info,
    location,
    createShop,
    setCreateShop,
    loading: connectionsLoading,
    createPost,
    setCreatePost,
    handleTabSelect,
    getActiveTabData,
    inputChange,
    userDatahandler,
    widthHandler,
  };
};
